import { useLatest } from 'ahooks';
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Tooltip } from '@meitu/saas-ui';
import { clientInfo } from '@meitu/pc-bridge';
import IconFont from '@meitu/xdesign-icon-font';
import { IFramework } from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { getLayerViewNode } from '@/utils/layer-kit';
import { IImageLayerProps, ILayerProps } from '@/version-types';
import { getImageCutoutInfo, useImageCutoutState } from '../states/image-cutout-state';
import './image-cutout.less';

const SPACE = 35;
// 免费下的宽度
const WIDTH_FREE = 82;
// 付费下的宽度
const WIDTH = 186;

interface IProps {
    isFree: boolean;
    // 是否静默扣除场景（需要正常扣除张数，但是不需要相关提示），目前主要用在企业版场景下
    isSilentMode: boolean;
    numLeft: number;
    onCancel: Function;
    onOptimize: Function;
    onConfirm: Function;
    beforeConfirm: Function;
    isIntl?: boolean;
    // 针对是国内web且非会员无张数用户
    isInternalUserAndNotVipAndNotLeftNum?: boolean;
    // 是否为国内 web 端会员
    isInternalUserVip?: boolean;
    leftFreeNum?: number;
    freeNum?: number;
    hideCutoutOptimize?: boolean;
    framework: IFramework;
    trackFunc?: string;
}

const ImageCutout: React.FC<IProps> = ({
    isFree,
    isSilentMode,
    numLeft = 0,
    onCancel,
    onOptimize,
    onConfirm,
    beforeConfirm,
    isIntl,
    isInternalUserAndNotVipAndNotLeftNum,
    isInternalUserVip,
    leftFreeNum = 0,
    freeNum = 0,
    hideCutoutOptimize,
    framework,
    trackFunc,
}) => {
    const { $t } = useLocaleStore();

    const imageCutoutInfo = useImageCutoutState()((state) => state.imageCutoutInfo);
    const setImageCutoutInfo = useImageCutoutState()((state) => state.setImageCutoutInfo);
    const setIsImageCutout = useImageCutoutState()((state) => state.setIsImageCutout);
    const isImageCutout = useImageCutoutState()((state) => state.isImageCutout);
    const { originImage, resultImage, isCutoutMainLayer } = imageCutoutInfo;
    const [activeLayer, setActiveData] = useState<ILayerProps | null>(framework.operate.getActivePickLayer());
    const [activeLayerId, setActiveDataId] = useState<string | null>(
        framework.operate.getActivePickLayer()?.id || null,
    );
    const originLayerDataRef = useRef<ILayerProps | null>(null);
    const currentActiveIdRef = useRef<string | null>(null);

    useEffect(() => {
        const handle = framework.events.onActiveLayerDataUpdate((layer, pickLayer) => {
            setActiveDataId(pickLayer?.id || layer?.id || null);
        });

        return () => {
            framework.events.off(handle);
        };
    }, []);

    useEffect(() => {
        if (activeLayerId) {
            const layer = framework.getLayer(activeLayerId, true);
            setActiveData(layer);
        } else {
            if (originLayerDataRef.current) {
                setActiveData(null);
                originLayerDataRef.current = null;
                resetImageCutoutInfo();
                setIsImageCutout(false);
                framework.operate.enableOperate();
            }
        }
    }, [activeLayerId]);

    const [imageCutoutStyles, setImageCutoutStyles] = useState<React.CSSProperties>({});
    const activeId = activeLayer?.id || '';
    const consumePrice = 1;

    const resetImageCutoutInfo = () => {
        setImageCutoutInfo(getImageCutoutInfo());
    };
    const cancelLogic = () => {
        setIsImageCutout(false);
        resetImageCutoutInfo();
        if (!originLayerDataRef.current) return;
        if (framework.getLayer(originLayerDataRef.current.id)) {
            framework.stage.addOrUpdate(cloneDeep(originLayerDataRef.current));
            framework.operate.setActiveLayer(originLayerDataRef.current.id);
        }
        originLayerDataRef.current = null;
    };

    const handleCancel = async () => {
        if (onCancel) await onCancel();
        cancelLogic();
        framework.operate.enableOperate();
        framework.operate.globalMask.hide();
    };

    const handleConfirm = async () => {
        if (onConfirm) onConfirm();
        const oldInfo = cloneDeep(originLayerDataRef.current);
        // const newInfo = cloneDeep(oldInfo);
        if (oldInfo) {
            const newInfo = framework.getLayer(oldInfo.id, true);
            if (newInfo) {
                const newInfoData = newInfo.data as IImageLayerProps;
                const oldInfoData = oldInfo.data as IImageLayerProps;
                newInfoData.url = resultImage;
                oldInfoData.url = originImage;
                framework.stage.addOrUpdate(newInfo);
                framework.history.write(oldInfo, newInfo, trackFunc);
            }
        }
        resetImageCutoutInfo();
        originLayerDataRef.current = null;
        framework.operate.enableOperate();
        framework.operate.globalMask.hide();
    };

    const checkConfirm = useLatest(() => {
        if (isFree && !isCutoutMainLayer) {
            handleConfirm();
            return;
        }

        if (clientInfo.isClient) {
            // PC内嵌
            checkConfirmInPc();
            return;
        }

        Modal.confirm({
            title: `${$t((v) => v.imageMatting.confirmText)} ${isIntl ? '' : '？'}`,
            content: isIntl ? (
                <span className="c-image-cutout-modal__content">{$t((v) => v.imageMatting.applyTips)}</span>
            ) : !isSilentMode && !isInternalUserAndNotVipAndNotLeftNum && !isInternalUserVip ? (
                <div>
                    <span className="c-image-cutout-modal__content">
                        {$t((v) => v.imageMatting.consumeAfterApply)}：{consumePrice}
                    </span>
                    <span>
                        ({$t((v) => v.imageMatting.remainPage)}：{numLeft})
                    </span>
                </div>
            ) : null,
            hideIcon: true,
            closable: false,
            maskClosable: false,
            className: 'c-image-cutout-modal',
            centered: true,
            okText:
                !isIntl && !numLeft && !isInternalUserAndNotVipAndNotLeftNum && !isInternalUserVip
                    ? $t((v) => v.imageMatting.recharge)
                    : undefined,
            onOk: async () => {
                framework.operate.globalMask.show({ loading: true });
                const status = await beforeConfirm('ok');
                // 校验不通过，则保持当前状态
                if (status !== undefined && !status) {
                    if (originLayerDataRef.current?.id) {
                        framework.operate.setActiveLayer(originLayerDataRef.current.id);
                    }
                    framework.operate.globalMask.hide();
                    return;
                }
                handleConfirm();
            },
            onCancel: () => {
                handleCancel();
            },
        });
    });

    const handleApply = async () => {
        if (isFree) {
            handleConfirm();
            return;
        }

        if (clientInfo.isClient) {
            // PC内嵌
            checkConfirmInPc();
            return;
        }

        framework.operate.globalMask.show({ loading: true });

        const status = await beforeConfirm('apply');
        // 校验不通过，则保持当前状态
        if (status !== undefined && !status) {
            framework.operate.globalMask.hide();
            return;
        }
        handleConfirm();
    };

    // PC内嵌场景下抠图的确认弹窗逻辑
    const checkConfirmInPc = () => {
        Modal.confirm({
            title: leftFreeNum ? '智能抠图限时免费试用' : '开通会员抠图不受限',
            content: (
                <div>
                    活动期间，每日可享{freeNum}
                    次智能抠图免费试用，您确定要试用并消耗1次试用吗？
                    <div style={{ marginTop: 20 }}>
                        今日已试用：{freeNum - leftFreeNum}/{freeNum}
                    </div>
                </div>
            ),
            centered: true,
            closable: false,
            maskClosable: false,
            okText: !leftFreeNum ? '立即开通' : undefined,
            onOk: async () => {
                const status = await beforeConfirm('ok');
                // 校验不通过，则保持当前状态
                if (status !== undefined && !status) {
                    if (originLayerDataRef.current?.id) {
                        framework.operate.setActiveLayer(originLayerDataRef.current.id);
                    }
                    return;
                }
                handleConfirm();
            },
            onCancel: () => {
                handleCancel();
            },
        });
    };

    // 抠图优化
    const handleOptimize = async () => {
        // sept1: 进入优化
        onOptimize();

        // step2: 取消应用
        cancelLogic();
    };

    useEffect(() => {
        if (!isImageCutout) return;
        // 已经处理抠图 但是失去选中 应用效果
        if (!activeLayer && isImageCutout && resultImage && !isCutoutMainLayer) {
            // checkConfirm();
            return;
        }
        // 已经处理抠图 但是选中其他素材 应用效果
        if (activeLayer && isImageCutout && resultImage && currentActiveIdRef.current !== activeLayer.id) {
            // checkConfirm();
            return;
        }
        if (activeLayer || isCutoutMainLayer) {
            const originIndex = framework.stage.getActiveOriginIndex();
            const mainLayer = framework.getMainLayer(originIndex);
            const cutoutLayer = isCutoutMainLayer ? mainLayer : activeLayer;

            originLayerDataRef.current = cloneDeep(cutoutLayer!);
            if (!originLayerDataRef.current) return;
            const data = originLayerDataRef.current.data as IImageLayerProps;
            data.url = originImage;
            currentActiveIdRef.current = activeId;
        }
    }, [activeLayer, isImageCutout, resultImage, isCutoutMainLayer]);

    useEffect(() => {
        // 失焦抠图确认
        const doConfirm = (e: MouseEvent) => {
            const targetEl = e.target as HTMLElement;
            // 确认弹窗点击
            if (
                document.querySelector('.c-image-cutout-buttons')?.contains(targetEl) || // 点击取消/应用面板
                document.querySelector('.c-image-cutout-tooltip')?.contains(targetEl) || // 点击取消/应用面板
                document.querySelector('.buy-vip-modal')?.contains(targetEl) || // 点击订阅弹窗蒙层
                document.querySelector('.saas-subscription-popup')?.contains(targetEl) || // 点击订阅弹窗
                document.querySelector('.x-design-subscribe-popup-mask')?.contains(targetEl) || // 点击海外订阅蒙层
                document.querySelector('.x-design-subscribe-popup')?.contains(targetEl) || // 点击海外订阅弹窗
                document.querySelector('.starii-account-view')?.contains(targetEl) || // 点击海外登录弹窗
                document.querySelector('.xdesign-modal')?.contains(targetEl) || // 点击海外弹窗
                (document.querySelector('.aiMatting-Adjust')?.contains(targetEl) &&
                    !targetEl.classList.contains('matting-confirm') &&
                    !targetEl.parentElement?.classList.contains('matting-confirm')) || // 点击调整智能抠图
                document.querySelector('#extension-container')?.contains(targetEl) || // 点击 SDK 弹窗
                document.querySelector('.matting-plugin-view')?.contains(targetEl) || // 点击抠图 SDK 视图
                document.querySelector('.mask-loading-wrap')?.contains(targetEl) || // 点击 loading 蒙层
                Array.from(document.querySelectorAll('.p-editor__mask')).find((ele) => ele?.contains(targetEl)) || // 点击 mask 蒙层
                Array.from(document.querySelectorAll('.ant-modal-root')).find((ele) => ele?.contains(targetEl)) // 点击确认弹窗模块
            ) {
                return;
            }
            // console.log('ot cutout', { isImageCutout, resultImage });
            if (isImageCutout && resultImage) {
                checkConfirm.current();
            }
        };

        document.addEventListener('mousedown', doConfirm, true);
        return () => {
            document.removeEventListener('mousedown', doConfirm, true);
        };
    }, [isImageCutout, resultImage]);

    useEffect(() => {
        if (!activeId || !isImageCutout) return;
        const imageBtnStyles = () => {
            const elem = getLayerViewNode(framework.identity, activeId);
            const { left, top, width } = (elem as HTMLDivElement).getBoundingClientRect();

            setImageCutoutStyles({
                top: top - SPACE,
                left:
                    left +
                    width -
                    (isFree || isInternalUserAndNotVipAndNotLeftNum || isSilentMode ? WIDTH_FREE : WIDTH),
                visibility: isImageCutout ? 'initial' : 'hidden',
            });
        };
        imageBtnStyles();
        window.addEventListener('resize', imageBtnStyles);
        return () => {
            window.removeEventListener('resize', imageBtnStyles);
        };
    }, [activeId, isImageCutout]);

    const renderTooltipContent = () => (
        <div className="c-image-cutout-tooltip__body">
            <div className="c-image-cutout-tooltip__info">
                <span>
                    {$t((v) => v.imageMatting.cutoutRemainPage)}：{numLeft}
                </span>
                <span className="c-image-cutout-tooltip__line"></span>
                <span>
                    {$t((v) => v.imageMatting.consumeAfterApply)}：{consumePrice}
                </span>
            </div>
            <div className="c-image-cutout-tooltip__desc">{$t((v) => v.imageMatting.remainPageConfirm)}</div>
        </div>
    );

    if (!isImageCutout || !resultImage || !activeLayer || isCutoutMainLayer) return null;

    return (
        <div className="c-image-cutout-buttons" style={{ ...imageCutoutStyles }}>
            <div className="c-image-cutout-btn" onClick={(e) => handleCancel()}>
                {$t((v) => v.imageMatting.cancel)}
            </div>
            <div className="c-image-cutout-btn c-image-cutout-confirm">
                <span onClick={handleApply}>{$t((v) => v.imageMatting.apply)}</span>
                {!isIntl &&
                !isFree &&
                !isInternalUserAndNotVipAndNotLeftNum &&
                !clientInfo.isClient &&
                !isSilentMode ? (
                    <>
                        <span className="c-image-cutout-tips" onClick={handleApply}>
                            （{$t((v, { r }) => r(v.imageMatting.consumeOne, [consumePrice]))}）
                        </span>
                        <Tooltip
                            placement="top"
                            overlayClassName="c-image-cutout-tooltip"
                            title={renderTooltipContent()}
                        >
                            <span>
                                <IconFont name="InfoCircleBold" color="#939599" fontSize={14} />
                            </span>
                        </Tooltip>
                    </>
                ) : null}
            </div>
            {!hideCutoutOptimize ? (
                <div className="c-image-cutout-btn" onClick={(e) => handleOptimize()}>
                    {$t((v) => v.imageMatting.mattingOptimize)}
                </div>
            ) : null}
        </div>
    );
};

export default React.memo(ImageCutout);
